// MessageTemplateTokenPicker.js — Token insertion popover for message template editor.
//
// Reads MESSAGE_TEMPLATE_FIELD_GROUPS from the messaging registry (plan 14-02).
// Structurally impossible to surface HTML-only tokens (MTPL-04): the picker
// only ever reads from the messaging-safe allowlist, never from the email registry.
//
// Props:
//   onInsert   {Function}  — (token: string) => void — called when a token row is clicked
//   disabled   {boolean}   — optional; disables the trigger button

import React from 'react';
import { MESSAGE_TEMPLATE_FIELD_GROUPS } from '../../../utils/messageTemplateFields';

// ─── Icon ──────────────────────────────────────────────────────────────────────

function BracesIcon({ className = 'w-4 h-4' }) {
    return (
        <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M7 3H5a2 2 0 0 0-2 2v2a2 2 0 0 1-2 2 2 2 0 0 1 2 2v2a2 2 0 0 0 2 2h2" />
            <path d="M13 3h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2 2 2 0 0 0-2 2v2a2 2 0 0 1-2 2h-2" />
        </svg>
    );
}

// ─── MessageTemplateTokenPicker ────────────────────────────────────────────────

export default function MessageTemplateTokenPicker({ onInsert, disabled = false }) {
    const [open, setOpen] = React.useState(false);
    const containerRef = React.useRef(null);

    // Close on outside click
    React.useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleSelect = (token) => {
        onInsert?.(token);
        setOpen(false);
    };

    return (
        <div ref={containerRef} className="relative inline-block">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm border border-[color:var(--border-color)] text-[color:var(--text-primary)] bg-transparent hover:bg-[color:var(--surface-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <BracesIcon />
                Insert token
            </button>

            {open && (
                <div
                    role="listbox"
                    aria-label="Insert token"
                    className="absolute left-0 top-10 z-50 w-72 rounded-lg border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] shadow-lg overflow-hidden"
                >
                    {MESSAGE_TEMPLATE_FIELD_GROUPS.map((group) => (
                        <div key={group.label}>
                            <p className="px-3 pt-2 pb-1 text-xs font-semibold text-[color:var(--text-muted)] uppercase tracking-wide">
                                {group.label}
                            </p>
                            {group.fields.map((field) => (
                                <button
                                    key={field.token}
                                    type="button"
                                    role="option"
                                    aria-selected="false"
                                    onClick={() => handleSelect(field.token)}
                                    className="w-full text-left px-3 py-2 flex flex-col hover:bg-[color:var(--surface-hover)] transition-colors"
                                >
                                    <span className="text-xs font-mono text-[color:var(--color-accent)]">
                                        {field.token}
                                    </span>
                                    <span className="text-xs text-[color:var(--text-muted)] mt-0.5">
                                        {field.description}
                                    </span>
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── MessageTemplateTokenRail ──────────────────────────────────────────────────
// Persistent right-rail token inspector, mirroring the email template editor's
// TokenInspector. Reads the same messaging-safe allowlist as the popover above
// (MESSAGE_TEMPLATE_FIELD_GROUPS), so HTML-only tokens remain structurally
// impossible to surface (MTPL-04). Hidden on small screens — the popover picker
// (above) is the mobile fallback.
//
// Props:
//   onInsert   {Function}  — (token: string) => void — called when a token row is clicked

export function MessageTemplateTokenRail({ onInsert }) {
    return (
        <div
            className="hidden md:flex flex-col border-l border-[color:var(--border-color)] min-h-0 shrink-0"
            style={{ width: 280, minWidth: 280, background: 'var(--surface-muted)' }}
        >
            <div
                className="flex items-center px-3 border-b border-[color:var(--border-color)] shrink-0"
                style={{ height: 40 }}
            >
                <span className="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                    Tokens
                </span>
            </div>
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
                {MESSAGE_TEMPLATE_FIELD_GROUPS.map((group) => (
                    <div key={group.label}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-muted)] px-3 pt-3 pb-1">
                            {group.label}
                        </p>
                        {group.fields.map((field) => (
                            <button
                                key={field.token}
                                type="button"
                                onClick={() => onInsert?.(field.token)}
                                title={`Insert ${field.token} at cursor`}
                                aria-label={`Insert ${field.token} at cursor`}
                                className="w-full text-left px-3 py-2 flex flex-col hover:bg-[color:var(--surface-hover)] transition-colors"
                            >
                                <code className="text-xs font-mono break-all leading-[1.4] text-[color:var(--color-accent)]">
                                    {field.token}
                                </code>
                                {field.description && (
                                    <span className="text-xs text-[color:var(--text-muted)] leading-[1.4] mt-0.5 break-words">
                                        {field.description}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}
